import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface FilterState {
  therapeuticArea: string;
  therapeuticDomain: string; // New: Domain-level filtering (e.g., 'Dermatology')
  region: string[]; // Changed from string to string[] for multi-select
  yearRange: [number, number];
  countries: string[]; // Added for country filtering
  drugClasses: string[];
  therapeuticCategories: string[];
  phases: string[];
  classifications: string[];
  indicationTypes: string[];
  pipelineStatus: 'active' | 'discontinued' | 'marketed' | 'all';
  developers: string[]; // currently unused; kept for forward-compat
  companySearchMulti: string[]; // Multi-select company search
  drugNameSearch: string;
  classificationFilter: string[];
  phaseFilter: string[];
  // New Psoriasis-specific filters
  indications: string[];
  mechanisms: string[];
  routes: string[];
  // Market filters
  routesOfAdministration: string[];
  distributionChannels: string[];
  // New market filters
  drugTypes: string[];
  mechanismsOfAction: string[];
  smallMoleculeDrugClasses: string[];
  prescriptionStatuses: string[];
  patentStatuses: string[];
}

interface FilterContextType {
  filters: FilterState;
  updateFilter: (key: keyof FilterState, value: any) => void;
  resetFilters: () => void;
  resetTASpecificFilters: () => void;
  // Derived flags for mutual exclusivity
  isClassMode: boolean;
  isCategoryMode: boolean;
  isRouteMode: boolean;
  isDistributionMode: boolean;
  isDrugTypeMode: boolean;
  isMechanismMode: boolean;
  isSmallMoleculeMode: boolean;
  isPrescriptionStatusMode: boolean;
  isPatentStatusMode: boolean;
  isUnfilteredMode: boolean;
  hierarchies: { [dimension: string]: { [child: string]: string } };
  setHierarchies: (hierarchies: { [dimension: string]: { [child: string]: string } }) => void;
}

const defaultFilters: FilterState = {
  therapeuticArea: '', // Default to empty string, will be set by Initializer
  therapeuticDomain: 'Dermatology', // Default to Dermatology domain
  region: [], // Changed from ['All'] to [] - empty means show all regions
  yearRange: [2019, 2025] as [number, number], // Default to trial version range for better compatibility
  countries: [], // Added for country filtering - empty means show all countries
  drugClasses: [],
  therapeuticCategories: [],
  phases: [],
  classifications: [],
  indicationTypes: [],
  pipelineStatus: 'active',
  developers: [],
  companySearchMulti: [],
  drugNameSearch: '',
  classificationFilter: [],
  phaseFilter: [],
  // New Psoriasis-specific filters
  indications: [],
  mechanisms: [],
  routes: [],
  // Market filters
  routesOfAdministration: [],
  distributionChannels: [],
  // New market filters
  drugTypes: [],
  mechanismsOfAction: [],
  smallMoleculeDrugClasses: [],
  prescriptionStatuses: [],
  patentStatuses: []
};

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const FilterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [filters, setFilters] = useState<FilterState>(() => {
    // Ensure yearRange is always a proper array
    const initialFilters = { ...defaultFilters };
    if (!Array.isArray(initialFilters.yearRange) || initialFilters.yearRange.length !== 2) {
      initialFilters.yearRange = [2019, 2025] as [number, number];
    }
    return initialFilters;
  });

  const [hierarchies, setHierarchies] = useState<{ [dimension: string]: { [child: string]: string } }>({});

  // Auto-set therapeutic area when user data is available
  useEffect(() => {
    // This will be handled by the components that have access to user data
    // For now, we'll keep the default
  }, []);

  // Helper function to get available year range for a therapeutic area
  const getAvailableYearRange = (therapeuticArea: string): { min: number; max: number } => {
    // Dermatology domain
    if (therapeuticArea === 'Dermatology - In Sight' || therapeuticArea === 'Dermatology') {
      return { min: 2020, max: 2035 };
    } else if (therapeuticArea === 'Dermatology Pipeline') {
      return { min: 2020, max: 2035 };
    } else if (therapeuticArea === 'Psoriasis - Focus' || therapeuticArea === 'Psoriasis') {
      return { min: 2020, max: 2035 };
    } else if (therapeuticArea === 'Skin Cancer - Trial Version') {
      return { min: 2019, max: 2025 };
    } else if (therapeuticArea === 'Skin Cancer - Focus' || therapeuticArea === 'Skin Cancer') {
      return { min: 2019, max: 2034 };
    // Ophthalmology domain
    } else if (therapeuticArea === 'Ophthalmology - In Sight' || therapeuticArea === 'Ophthalmology') {
      return { min: 2021, max: 2036 };
    } else if (therapeuticArea === 'Retinal - Focus' || therapeuticArea === 'Retinal Disorders') {
      return { min: 2021, max: 2036 };
    } else if (therapeuticArea === 'Glaucoma - Focus' || therapeuticArea === 'Glaucoma') {
      return { min: 2021, max: 2036 };
    // Cell, Gene & RNA domain
    } else if (therapeuticArea === 'Cell, Gene & RNA Therapies - In Sight' || therapeuticArea === 'Cell, Gene & RNA Therapies') {
      return { min: 2020, max: 2036 };
    } else {
      // Default fallback - use trial version range as it's most restrictive
      return { min: 2019, max: 2025 };
    }
  };

  // Helper function to get default year range (2026 to max, or max if max < 2026)
  const getDefaultYearRange = (therapeuticArea: string): [number, number] => {
    const available = getAvailableYearRange(therapeuticArea);
    
    // Special case: Skin Cancer - Trial Version should use full available range
    if (therapeuticArea === 'Skin Cancer - Trial Version') {
      return [available.min, available.max];
    }
    
    const defaultStart = 2026;
    const defaultEnd = Math.min(2036, available.max); // Use 2036 or max available, whichever is smaller
    
    // If max available is less than 2026, use the max available year
    if (available.max < defaultStart) {
      return [available.max, available.max];
    }
    
    // Ensure start year is not less than min available
    const startYear = Math.max(defaultStart, available.min);
    
    return [startYear, defaultEnd];
  };

  // Ensure year range matches therapeutic area on initial load and when it changes
  // This is a safety net in case updateFilter doesn't catch all cases
  useEffect(() => {
    // Skip if therapeutic area is empty (not yet initialized)
    if (!filters.therapeuticArea) {
      return;
    }

    const availableRange = getAvailableYearRange(filters.therapeuticArea);
    const defaultRange = getDefaultYearRange(filters.therapeuticArea);
    
    // Defensive check before accessing yearRange
    const currentYearRange = Array.isArray(filters.yearRange) && filters.yearRange.length >= 2 
      ? filters.yearRange 
      : defaultRange;
    
    // Validate current range is within available bounds
    const [currentStart, currentEnd] = currentYearRange;
    const isValidRange = currentStart >= availableRange.min && 
                         currentEnd <= availableRange.max && 
                         currentStart <= currentEnd;
    
    // Update if range is invalid or doesn't match the default
    if (!isValidRange || currentStart !== defaultRange[0] || currentEnd !== defaultRange[1]) {
      console.log('Updating yearRange to default for therapeutic area:', {
        therapeuticArea: filters.therapeuticArea,
        oldRange: currentYearRange,
        newRange: defaultRange,
        availableRange
      });
      setFilters(prev => ({
        ...prev,
        yearRange: defaultRange as [number, number]
      }));
    }
  }, [filters.therapeuticArea]);

  const updateFilter = (key: keyof FilterState, value: any) => {
    setFilters(prev => {
      let newFilters = { ...prev, [key]: value };
      
      // Reset TA-specific filters when therapeutic area changes
      if (key === 'therapeuticArea') {
        newFilters.indications = [];
        newFilters.mechanisms = [];
        newFilters.routes = [];
        newFilters.classificationFilter = [];
        newFilters.phaseFilter = [];
        newFilters.routesOfAdministration = [];
        newFilters.distributionChannels = [];
        
        // Reset pipeline status to "active" if current status is not supported by new therapeutic area
        // Only Dermatology supports "marketed" status
        const supportsMarketed = value === 'Dermatology - In Sight' || value === 'Dermatology';
        if (newFilters.pipelineStatus === 'marketed' && !supportsMarketed) {
          newFilters.pipelineStatus = 'active';
        }
        
        // Set default year range (2026 to max available, or max if max < 2026)
        newFilters.yearRange = getDefaultYearRange(value);
      }
      
      // Mutual exclusivity logic for market filters
      if (Array.isArray(value) && value.length > 0) {
        switch (key) {
          case 'drugClasses':
            newFilters.therapeuticCategories = [];
            newFilters.drugTypes = [];
            newFilters.mechanismsOfAction = [];
            newFilters.smallMoleculeDrugClasses = [];
            break;
          case 'therapeuticCategories':
            newFilters.drugClasses = [];
            newFilters.drugTypes = [];
            newFilters.mechanismsOfAction = [];
            newFilters.smallMoleculeDrugClasses = [];
            break;
          case 'drugTypes':
            newFilters.drugClasses = [];
            newFilters.therapeuticCategories = [];
            newFilters.mechanismsOfAction = [];
            newFilters.smallMoleculeDrugClasses = [];
            break;
          case 'mechanismsOfAction':
            newFilters.drugClasses = [];
            newFilters.therapeuticCategories = [];
            newFilters.drugTypes = [];
            newFilters.smallMoleculeDrugClasses = [];
            break;
          case 'smallMoleculeDrugClasses':
            newFilters.drugClasses = [];
            newFilters.therapeuticCategories = [];
            newFilters.drugTypes = [];
            newFilters.mechanismsOfAction = [];
            break;
          case 'routesOfAdministration':
            newFilters.distributionChannels = [];
            break;
          case 'distributionChannels':
            newFilters.routesOfAdministration = [];
            break;
        }
      }
      
      return newFilters;
    });
  };

  const resetFilters = () => {
    setFilters(prev => {
      const newYearRange: [number, number] = prev.therapeuticArea 
        ? getDefaultYearRange(prev.therapeuticArea)
        : [2019, 2025] as [number, number]; // Fallback if no therapeutic area is set
      return {
        ...defaultFilters,
        therapeuticArea: prev.therapeuticArea, // Keep the current TA
        therapeuticDomain: prev.therapeuticDomain, // Keep the current domain
        yearRange: newYearRange,
      };
    });
  };

  const resetTASpecificFilters = () => {
    setFilters(prev => ({
      ...prev,
      indications: [],
      mechanisms: [],
      routes: [],
      classificationFilter: [],
      phaseFilter: []
    }));
  };

  // Derived flags for mutual exclusivity
  const isClassMode = filters.drugClasses.length > 0;
  const isCategoryMode = filters.therapeuticCategories.length > 0;
  const isRouteMode = filters.routesOfAdministration.length > 0;
  const isDistributionMode = filters.distributionChannels.length > 0;
  const isDrugTypeMode = filters.drugTypes.length > 0;
  const isMechanismMode = filters.mechanismsOfAction.length > 0;
  const isSmallMoleculeMode = filters.smallMoleculeDrugClasses.length > 0;
  const isPrescriptionStatusMode = filters.prescriptionStatuses.length > 0;
  const isPatentStatusMode = filters.patentStatuses.length > 0;
  const isUnfilteredMode = !isClassMode && !isCategoryMode && !isRouteMode && !isDistributionMode && !isDrugTypeMode && !isMechanismMode && !isSmallMoleculeMode && !isPrescriptionStatusMode && !isPatentStatusMode;

  return (
    <FilterContext.Provider value={{ 
      filters, 
      updateFilter, 
      resetFilters,
      resetTASpecificFilters,
      isClassMode,
      isCategoryMode,
      isRouteMode,
      isDistributionMode,
      isDrugTypeMode,
      isMechanismMode,
      isSmallMoleculeMode,
      isPrescriptionStatusMode,
      isPatentStatusMode,
      isUnfilteredMode,
      hierarchies,
      setHierarchies
    }}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilters = () => {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
};